import React, {
  useContext,
  useState,
  forwardRef,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import './FooterConfirm.scss';

import { useHistory } from 'react-router-dom';
import { useDiscount } from 'context/DiscountProvider';
import { useEstimatedFare } from 'context/EstimatedFareProvider';
import { useFetchDiscount } from 'hooks/useFetchDiscount';
import { EstimatedFareItem } from 'api/taxiTrip/model/FEModel';
import { useLoadingState } from 'hooks/useLoadingState';
import { appendValidAppVersionQueryToUrl } from 'utils';

import Modali, { useModali } from 'components/Modal';
import { setToastAppear } from 'components/Toast/Toast';
import CarTypePopup from 'components/CarTypePopup';
import { DraggablePopupRef } from 'components/Popup/DraggablePopup';
import { CreditCardImage } from '../../pages/creditCard/component/CreditCardImage';

import { placeContext } from '../../place.js';

import { CreditCardInfo } from '../../pages/creditCard/selectCreditCardPage';
import * as taxiTripAPI from 'api/taxiTrip';

interface FooterConfirmProps {
  isOnlinePay: boolean;
  isLoadingFare: boolean;
  children: React.ReactNode;
  isDiscountOpen: boolean;
  isFeeDetailsOpen: boolean;
  onBottomPopupClose: () => void;
  setIsDiscountOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFeeDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCard: CreditCardInfo | null;
  setSelectedCard: React.Dispatch<React.SetStateAction<CreditCardInfo | null>>;
  setEstimateFareTimeoutControl: React.Dispatch<React.SetStateAction<{ action: string }>>;
  setPaymentOriginPage: React.Dispatch<React.SetStateAction<string>>;
  isLoadingCarTypeFareList: boolean;
  setIsLoadingCarTypeFareList: React.Dispatch<React.SetStateAction<boolean>>;
  isKeyboardVisible: boolean;
}

interface DiscountedCarTypeFare extends EstimatedFareItem {
  discountedFare: {
    originalFare: number;
    discountedOrderAmount: number;
    discountAmount: number;
    hasRange: boolean;
    rangeOriginalAmount: { min: number; max: number };
    rangeDiscountAmount: { min: number; max: number };
  };
}

const cardTypeMap = {
  VISA: 'VISA',
  MasterCard: 'Mastercard',
  JCB: 'JCB',
  AMEX: 'Amex',
  'Union Pay': 'UnionPay',
};

const FooterConfirm = forwardRef<HTMLDivElement, FooterConfirmProps>(
  (
    {
      isOnlinePay,
      children,
      isLoadingFare,
      isDiscountOpen,
      isFeeDetailsOpen,
      onBottomPopupClose,
      setIsDiscountOpen,
      setIsFeeDetailsOpen,
      selectedCard,
      setSelectedCard,
      isLoadingCarTypeFareList,
      setIsLoadingCarTypeFareList,
      setEstimateFareTimeoutControl,
      setPaymentOriginPage,
      isKeyboardVisible,
    },
    ref
  ) => {
    const history = useHistory();
    const { discountTotal, discountDetails, isDiscountLoading } = useDiscount();
    const { getDiscountedOrderAmount, updateDiscountUsage } = useFetchDiscount();
    const { place } = useContext(placeContext);
    const { estimatedFare, carTypeFareList, isEstimatedFareLoading, setEstimatedFare } =
      useEstimatedFare();
    const { isLoading: isFareLoadingCombined, setLoading } = useLoadingState();

    const [isAnimating, setIsAnimating] = useState(false);
    const [isCarTypeOpen, setIsCarTypeOpen] = useState(false);
    const [discountedCarTypeFares, setDiscountedCarTypeFares] = useState<DiscountedCarTypeFare[]>(
      []
    ); // 用於存儲折扣後的車型資訊
    const isNeedSelectCheapestCarType = useRef(true);

    const issueBankText = useMemo(() => {
      // 超過20個字元時，只顯示前20個字元
      if (!selectedCard) return '';
      return selectedCard.issueBank.length > 20
        ? selectedCard.issueBank.slice(0, 20)
        : selectedCard.issueBank;
    }, [selectedCard]);

    const carTypePopupRef = useRef<DraggablePopupRef>(null);
    // 使用 useRef 儲存 getDiscountedOrderAmount 函數引用，避免無限循環
    const getDiscountedOrderAmountRef = useRef(getDiscountedOrderAmount);

    const [estimatedFareErrorModal, toggleEstimatedFareErrorModal] = useModali({
      animated: true,
      overlayClose: false,
      title: '連線異常',
      message: '服務目前異常，請稍後再試',
      buttons: [
        <Modali.Button
          key="ModaliButton"
          label="確認"
          isTwoButton
          isStyleDestructive
          onClick={() => toggleEstimatedFareErrorModal()}
        />,
      ],
    });

    const shouldShowCarTypePopup =
      place.originPlace && place.destinationPlace && isOnlinePay && isCarTypeOpen;

    const discountPopupTimer = useRef<NodeJS.Timeout | null>(null);

    const setDefaultCreditCard = useCallback(() => {
      taxiTripAPI
        .GetUserBindCard()
        .then(([res]) => {
          if (res) {
            const cards = res.data.data.cards;
            const formatedCreditCards: CreditCardInfo[] = cards.map((card) => ({
              ...card,
              id: card.guid,
              type: cardTypeMap[card.cardAssociation] || '',
              alt: card.cardAssociation,
              encryptedCardNumber: `*${card.lastFour}`,
              bank: card.issueBank,
            }));

            if (formatedCreditCards.length > 0) {
              const defaultCardIndex = formatedCreditCards.findIndex((card) => card.isDefault);
              if (defaultCardIndex > -1) {
                setSelectedCard(formatedCreditCards[defaultCardIndex]);
              }
            }
          }
        })
        .catch((error) => {
          console.log(error);
          setToastAppear('服務異常，請稍後再試');
        });
    }, [setSelectedCard]);

    function toggleDiscountPopup() {
      if (!estimatedFare?.fare) {
        if (!place.originPlace || !place.destinationPlace) {
          setToastAppear('請輸入上下車地點以使用優惠');
        } else if (!isLoadingFare) {
          toggleEstimatedFareErrorModal();
        }
        return;
      }
      if (isAnimating) {
        return;
      }
      setIsAnimating(true);
      setIsDiscountOpen((prevIsDiscountOpen) => {
        if (prevIsDiscountOpen) {
          onBottomPopupClose();
        }
        discountPopupTimer.current = setTimeout(() => {
          setIsAnimating(false);
        }, 300);
        return !prevIsDiscountOpen;
      });
      if (isFeeDetailsOpen) {
        setIsFeeDetailsOpen(false);
      }
    }

    async function handleRedirectToBindCard() {
      // 紀錄當前上下車地點
      sessionStorage.setItem('selectedPlace', JSON.stringify(place));
      const [result, error] = await taxiTripAPI.GetEcToken();
      if (error) {
        setToastAppear('服務異常，請稍後再試');
        return;
      }
      if (result) {
        const ecToken = encodeURIComponent(result.data.token);
        window.location.replace(
          appendValidAppVersionQueryToUrl(
            `${process.env.REACT_APP_EC_WEB_URI}addCreditCard?appAuth=${ecToken}&isFromTaxiAdd=true`
          )
        );
      }
    }

    const handleCardSelectorRedirect = () => {
      if (!selectedCard) {
        handleRedirectToBindCard();
        return;
      }
      carTypePopupRef.current?.setPosition('peek');
      setEstimateFareTimeoutControl({ action: 'stop' });
      setPaymentOriginPage('selectCreditCard');
      history.push('/SelectCreditCard');
    };

    function formatDiscountText(discountTotal: number) {
      return `已折 $${formatNumberWithCommas(discountTotal)}`;
    }

    function formatNumberWithCommas(number: number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    const getDiscountedFareObj = useCallback(
      (carType: EstimatedFareItem, discountedOrderAmount: number) => {
        const discountAmount = carType.fare - discountedOrderAmount;
        return {
          originalFare: carType.fare,
          discountedOrderAmount: discountedOrderAmount,
          discountAmount,
          hasRange: carType.fare_min !== carType.fare_max,
          rangeOriginalAmount: {
            min: carType.fare_min,
            max: carType.fare_max,
          },
          rangeDiscountAmount: {
            min: carType.fare_min - discountAmount < 0 ? 0 : carType.fare_min - discountAmount,
            max: carType.fare_max - discountAmount < 0 ? 0 : carType.fare_max - discountAmount,
          },
        };
      },
      []
    );

    // 異步計算折扣後價格
    async function calculateDiscounts({ discountCode, ticketInstanceId, useBonus }) {
      setIsLoadingCarTypeFareList(true);
      try {
        // 使用 Promise.all 同時計算所有車型的折扣
        const updatedResults = await Promise.all(
          carTypeFareList.map(async (carType) => {
            try {
              // 使用 ref 中的函數避免依賴項循環問題
              const discountedOrderAmount = await getDiscountedOrderAmountRef.current({
                fare: carType.fare,
                fareId: carType.fareId,
                discountCode: discountCode || undefined,
                ticketInstanceId: ticketInstanceId || undefined,
                useBonus: !!useBonus,
              });

              // 返回包含折扣資訊的車型
              return {
                ...carType,
                discountedFare: getDiscountedFareObj(carType, discountedOrderAmount),
              };
            } catch (error) {
              console.error(`計算車型 ${carType.productId} 折扣時出錯:`, error);
              // 發生錯誤時，返回原價
              return {
                ...carType,
                discountedFare: getDiscountedFareObj(carType, carType.fare),
              };
            }
          })
        );

        // 依照金額排序
        updatedResults.sort(
          (a, b) => a.discountedFare.discountedOrderAmount - b.discountedFare.discountedOrderAmount
        );

        // 比對選擇車型是否與原價一致為最便宜，只有在地址更動時需要取得
        if (
          estimatedFare.productId !== updatedResults[0].productId &&
          isNeedSelectCheapestCarType.current
        ) {
          console.log('選擇最便宜車型:', updatedResults[0]);
          isNeedSelectCheapestCarType.current = false;
          if (updatedResults[0]) {
            setEstimatedFare(updatedResults[0]);
            updateDiscountUsage({
              updatedOrderAmount: updatedResults[0].fare,
              fareId: updatedResults[0].fareId,
            });
          }
        }
        isNeedSelectCheapestCarType.current = false;

        // 更新車型列表
        setDiscountedCarTypeFares(updatedResults);
      } catch (error) {
        console.error('計算折扣時發生錯誤:', error);
      } finally {
        // 無論成功或失敗，都該關閉載入狀態
        setIsLoadingCarTypeFareList(false);
      }
    }

    useEffect(() => {
      setLoading('estimatedFare', isEstimatedFareLoading);
      setLoading('discount', isDiscountLoading);
      setLoading('carTypeFareList', isLoadingCarTypeFareList);
    }, [isEstimatedFareLoading, isLoadingCarTypeFareList, isDiscountLoading, setLoading]);

    useEffect(() => {
      if (isDiscountOpen) {
        setIsCarTypeOpen(false);
      } else {
        setIsCarTypeOpen(true);
      }
    }, [isDiscountOpen]);

    useEffect(() => {
      getDiscountedOrderAmountRef.current = getDiscountedOrderAmount;
    }, [getDiscountedOrderAmount]);

    useEffect(() => {
      if (isOnlinePay) {
        isNeedSelectCheapestCarType.current = true;
      }
    }, [isOnlinePay]);

    useEffect(() => {
      // 若無車型列表則跳過
      if (!carTypeFareList?.length) {
        setDiscountedCarTypeFares([]);
        return;
      }

      const { discountCode, ticketInstanceId, useBonus } = discountDetails;

      // 如果無折扣不計算
      if (
        !discountCode &&
        !ticketInstanceId &&
        !useBonus &&
        !isDiscountLoading &&
        !isEstimatedFareLoading
      ) {
        console.log('no discount');
        const tempDiscountedCarTypeFares = carTypeFareList.map((carType) => ({
          ...carType,
          discountedFare: getDiscountedFareObj(carType, carType.fare),
        }));
        setDiscountedCarTypeFares(tempDiscountedCarTypeFares);
        return;
      }

      if (!isDiscountLoading && !isEstimatedFareLoading) {
        calculateDiscounts(discountDetails);
      }
    }, [carTypeFareList, discountDetails, isDiscountLoading, isEstimatedFareLoading]);

    useEffect(() => {
      setDefaultCreditCard();
      return () => {
        if (discountPopupTimer.current) {
          clearTimeout(discountPopupTimer.current);
        }
      };
    }, []);

    return (
      <div
        className="footer-confirm__container"
        style={{ display: isKeyboardVisible ? 'none' : 'block' }}
      >
        {shouldShowCarTypePopup && (
          <div className="footer-confirm footer-confirm__car-type">
            <CarTypePopup
              ref={carTypePopupRef}
              title="選擇車種"
              popupBottom={130}
              discountedCarTypeFares={discountedCarTypeFares}
              initPosition="half"
              isLoading={isFareLoadingCombined}
            />
          </div>
        )}
        <div className="footer-confirm__wrap" ref={ref}>
          {isOnlinePay && (
            <div className="footer-confirm footer-confirm__payment">
              <div
                className="footer-confirm__credit-card"
                onClick={() => handleCardSelectorRedirect()}
              >
                {selectedCard && (
                  <div className="item__issue-bank__img">
                    <CreditCardImage
                      height="32px"
                      type={selectedCard.type}
                      alt={selectedCard.alt}
                    />
                  </div>
                )}
                <div className="item__issue-bank__text">
                  <p className="item__issue-bank__title">信用卡</p>
                  <p>
                    <span className={`item__issue-bank__name ${selectedCard ? '' : 'no-card'}`}>
                      {selectedCard
                        ? issueBankText + selectedCard.encryptedCardNumber
                        : '前往綁定信用卡'}
                    </span>
                    {selectedCard && <i className="icon-arrow-right"></i>}
                  </p>
                </div>
              </div>
              <div className="footer-confirm__discount" onClick={() => toggleDiscountPopup()}>
                <div className="footer-confirm__toggle">
                  {discountTotal > 0 && estimatedFare?.fareId ? (
                    <span className="footer-confirm__toggle--active">
                      {formatDiscountText(discountTotal)}
                    </span>
                  ) : (
                    <span>未套用優惠</span>
                  )}
                  {isDiscountOpen ? (
                    <i className="icon-arrow-up footer-confirm__toggle__icon"></i>
                  ) : (
                    <i className="icon-arrow-down footer-confirm__toggle__icon"></i>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="footer-confirm footer-confirm__fare">{children}</div>
          <Modali.Modal {...estimatedFareErrorModal}></Modali.Modal>
        </div>
      </div>
    );
  }
);

export default FooterConfirm;
